import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import './PasswordRecoveryGate.css';

type Props = { children: ReactNode };

type RecoveryData = {
  active: boolean;
  accessToken: string;
  type: string;
  error: string;
  errorDescription: string;
};

function parseRecovery(): RecoveryData {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const query = new URLSearchParams(window.location.search);

  const type = hash.get('type') || query.get('type') || '';
  const accessToken = hash.get('access_token') || query.get('access_token') || '';
  const error = hash.get('error') || query.get('error') || '';
  const errorDescription =
    hash.get('error_description') ||
    query.get('error_description') ||
    '';

  return {
    active: type === 'recovery' || Boolean(error && (window.location.hash || window.location.search)),
    accessToken,
    type,
    error,
    errorDescription: decodeURIComponent(errorDescription.replace(/\+/g, ' ')),
  };
}

export function PasswordRecoveryGate({ children }: Props) {
  const [recovery, setRecovery] = useState<RecoveryData>(() => parseRecovery());
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const refresh = () => setRecovery(parseRecovery());
    window.addEventListener('hashchange', refresh);
    window.addEventListener('popstate', refresh);
    return () => {
      window.removeEventListener('hashchange', refresh);
      window.removeEventListener('popstate', refresh);
    };
  }, []);

  const strength = useMemo(() => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Zא-ת]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9א-ת]/.test(password)) score++;
    return score;
  }, [password]);

  if (!recovery.active) return <>{children}</>;

  const goAdmin = () => {
    window.history.replaceState({}, '', '/admin');
    window.location.assign('/admin');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');

    if (!recovery.accessToken) {
      setMessage('קישור האיפוס אינו תקף או שפג תוקפו. יש לבקש קישור חדש.');
      return;
    }
    if (password.length < 8) {
      setMessage('הסיסמה צריכה להכיל לפחות 8 תווים.');
      return;
    }
    if (password !== confirm) {
      setMessage('הסיסמאות אינן תואמות.');
      return;
    }

    const url = import.meta.env.VITE_SUPABASE_URL?.trim()?.replace(/\/$/, '');
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

    if (!url || !key) {
      setMessage('חיבור Supabase אינו מוגדר באתר.');
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(`${url}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          apikey: key,
          Authorization: `Bearer ${recovery.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!res.ok) {
        let detail = '';
        try {
          const data = await res.json();
          detail = data?.msg || data?.message || data?.error_description || '';
        } catch {}
        throw new Error(detail || `HTTP ${res.status}`);
      }

      setSuccess(true);
      setMessage('הסיסמה עודכנה בהצלחה.');
      window.history.replaceState({}, '', '/admin');
      setTimeout(goAdmin, 1400);
    } catch (err) {
      const text = err instanceof Error ? err.message : '';
      if (/expired|invalid|token/i.test(text)) {
        setMessage('קישור האיפוס אינו תקף או שפג תוקפו. יש לבקש קישור חדש.');
      } else {
        setMessage(`לא הצלחנו לעדכן את הסיסמה${text ? `: ${text}` : '.'}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mielley-recovery-shell" dir="rtl">
      <section className="mielley-recovery-card" aria-labelledby="mielley-recovery-title">
        <div className="mielley-recovery-brand">
          <span>MIELLEY CONTROL CENTER</span>
          <strong>MIELLEY</strong>
        </div>

        {recovery.error ? (
          <>
            <div className="mielley-recovery-icon is-error" aria-hidden="true">!</div>
            <h1 id="mielley-recovery-title">הקישור אינו תקף</h1>
            <p className="mielley-recovery-lead">
              קישור האיפוס פג תוקף או שכבר נעשה בו שימוש.
            </p>
            <div className="mielley-recovery-alert">
              {recovery.errorDescription || 'יש לבקש קישור חדש מ-Supabase.'}
            </div>
            <button className="mielley-recovery-primary" type="button" onClick={goAdmin}>
              חזרה לכניסה לאדמין
            </button>
          </>
        ) : success ? (
          <>
            <div className="mielley-recovery-icon is-success" aria-hidden="true">✓</div>
            <h1 id="mielley-recovery-title">הסיסמה עודכנה</h1>
            <p className="mielley-recovery-lead">
              מעבירים אותך עכשיו למסך הכניסה לאדמין.
            </p>
            <button className="mielley-recovery-primary" type="button" onClick={goAdmin}>
              כניסה לאדמין
            </button>
          </>
        ) : (
          <>
            <div className="mielley-recovery-kicker">SECURE PASSWORD RESET</div>
            <h1 id="mielley-recovery-title">יצירת סיסמה חדשה</h1>
            <p className="mielley-recovery-lead">
              בחרי סיסמה חדשה לחשבון המנהל שלך.
            </p>

            <form onSubmit={submit} className="mielley-recovery-form">
              <label>
                <span>סיסמה חדשה</span>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>

              <div className="mielley-password-strength" aria-label="חוזק סיסמה">
                {[1,2,3,4].map(n => (
                  <i key={n} className={strength >= n ? 'is-on' : ''} />
                ))}
              </div>

              <label>
                <span>אימות סיסמה</span>
                <input
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
              </label>

              {message && (
                <div className={`mielley-recovery-alert ${success ? 'is-success' : ''}`}>
                  {message}
                </div>
              )}

              <button className="mielley-recovery-primary" type="submit" disabled={busy}>
                {busy ? 'מעדכנים...' : 'שמירת הסיסמה החדשה'}
              </button>
            </form>

            <div className="mielley-recovery-security">
              <span aria-hidden="true">◇</span>
              <div>
                <strong>חיבור מאובטח</strong>
                <small>הסיסמה מתעדכנת ישירות באמצעות Supabase Auth.</small>
              </div>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default PasswordRecoveryGate;
